import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { isEmpty } from 'lodash';
import styled from '@emotion/styled';

import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    ButtonGroupTestID,
    HeadingText50 as StepTitle,
    Input,
    InputType,
    pxToRem,
} from '@modernhealth/vital-components';

import { navigationService } from 'src/config/rootService';
import { mediaQueries } from 'src/shared/styles/variables';

import { CareNavigationSteps } from '../components/CareNavigationSteps';
import { useIsCareNavEnabledUser } from '../hooks/useIsCareNavEnabledUser';
import { CareNavigationRequest } from '../models/benefits';
import { CareNavigationDetailsStepTestId } from '../models/testsIds';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: pxToRem(500),
    marginTop: pxToRem(100),
    marginRight: 'auto',
    marginLeft: 'auto',
    [mediaQueries.sm]: {
        padding: '0.5rem',
    },
});

const ButtonGroupContainer = styled.div({
    marginTop: pxToRem(32),
    marginBottom: pxToRem(24),
});

const TitleContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: pxToRem(24),
    marginTop: pxToRem(32),
    maxWidth: '60%',
});

const FormContainer = styled.div({
    textAlign: 'center',
    width: '100%',
    [mediaQueries.sm]: {
        paddingBottom: 0,
    },
});

export const CareNavigationDetailsStep: React.FC = () => {
    const location = useLocation<CareNavigationRequest>();
    const { isUserEnabledFlow } = useIsCareNavEnabledUser();

    if (!isUserEnabledFlow) {
        navigationService.navigate('/dashboard');
    }

    if (isEmpty(location.state)) {
        navigationService.navigate('/care-navigation-request');
    }

    const { t } = useTranslation();
    const [requestState, setRequestState] = useState<CareNavigationRequest>(location.state);

    const isContinueButtonDisabled = () => {
        return requestState.message?.length === 0;
    };

    const handleContinue = () => {
        navigationService.navigate('/care-navigation-contact-method', requestState);
    };

    const handleBack = () => {
        navigationService.navigate('/care-navigation-request', {
            careNavigationRequest: requestState,
        });
    };

    return (
        <Wrapper>
            <CareNavigationSteps step={2} />

            <TitleContainer>
                <StepTitle centered>{t('benefits:care_navigation_details_step.title')}</StepTitle>
                <BodyTextMedium centered>
                    {t('benefits:care_navigation_details_step.description')}
                </BodyTextMedium>
            </TitleContainer>

            <FormContainer>
                <Input
                    fieldId="message"
                    testID={CareNavigationDetailsStepTestId.DetailsField}
                    type={InputType.TextArea}
                    label={t('benefits:care_navigation_details_step.input_placeholder')}
                    onChange={newValue =>
                        setRequestState({ ...requestState, message: newValue as string })
                    }
                    value={requestState.message}
                    height={212}
                />

                <ButtonGroupContainer>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            onClick={handleBack}
                            data-testid={ButtonGroupTestID.FirstButton}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="primary"
                            size="large"
                            onClick={handleContinue}
                            disabled={isContinueButtonDisabled()}
                            data-testid={ButtonGroupTestID.SecondButton}
                        >
                            {t('common.continue')}
                        </Button>
                    </ButtonGroup>
                </ButtonGroupContainer>
            </FormContainer>
        </Wrapper>
    );
};
